/**
 * Stone Investment Office Map Component
 * 
 * Interactive Leaflet map with greyscale/stone-colored aesthetic:
 * - CartoDB Positron greyscale tiles
 * - Stone-themed marker styling
 * - Luxury popup design
 * - Hover animations
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { Map as LeafletMap } from 'leaflet';

export interface OfficeMapLocation {
  /** Office name */
  name: string;
  /** Location subtitle */
  location: string;
  /** Address string */
  address: string;
  /** Latitude coordinate */
  latitude: number;
  /** Longitude coordinate */
  longitude: number;
  /** Optional phone number */
  phone?: string;
  /** Optional email */
  email?: string;
}

export interface StoneOfficeMapProps {
  /** Office location data */
  office: OfficeMapLocation;
  /** Map height */
  height?: string | number;
  /** Initial zoom level */
  zoom?: number;
  /** Custom className */
  className?: string;
  /** Translated UI strings */
  translations?: {
    loadingMap?: string;
    directions?: string;
  };
}

/**
 * StoneOfficeMap - Greyscale styled interactive map for office locations
 * 
 * Uses Leaflet with CartoDB Positron tiles for a minimal greyscale aesthetic
 * that complements the Stone Investment luxury theme.
 */
export function StoneOfficeMap({
  office,
  height = 400,
  zoom = 15,
  className = '',
  translations,
}: StoneOfficeMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${office.latitude},${office.longitude}`;

  // Inject custom styles for the map
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const styleId = 'stone-office-map-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .stone-marker {
          background: transparent !important;
          border: none !important;
        }
        .stone-marker-wrapper {
          position: relative;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .stone-marker-pulse {
          position: absolute;
          width: 24px;
          height: 24px;
          background: rgba(28, 25, 23, 0.3);
          border-radius: 50%;
          animation: stone-pulse 2s ease-out infinite;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        .stone-marker-pin {
          position: relative;
          width: 40px;
          height: 40px;
          background: #1c1917;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .stone-marker-pin:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 30px rgba(0, 0, 0, 0.4);
        }
        @keyframes stone-pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.6;
          }
          100% {
            transform: translate(-50%, -50%) scale(3);
            opacity: 0;
          }
        }
        /* Greyscale and sepia filter overlay for tiles - minimal stone aesthetic */
        .leaflet-tile-pane {
          filter: grayscale(20%) sepia(8%) contrast(0.92) brightness(1.02);
        }
        /* Stone-themed zoom controls */
        .leaflet-control-zoom {
          border: none !important;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
        }
        .leaflet-control-zoom a {
          background: rgba(255, 255, 255, 0.95) !important;
          color: #57534e !important;
          border: none !important;
          width: 32px !important;
          height: 32px !important;
          line-height: 32px !important;
          font-size: 16px !important;
          transition: all 0.2s ease !important;
        }
        .leaflet-control-zoom a:hover {
          background: #1c1917 !important;
          color: white !important;
        }
        .leaflet-control-zoom-in {
          border-bottom: 1px solid #e7e5e4 !important;
        }
        /* Stone-themed attribution */
        .leaflet-control-attribution {
          background: rgba(255, 255, 255, 0.8) !important;
          font-family: ui-sans-serif, system-ui, sans-serif !important;
          font-size: 10px !important;
          padding: 2px 6px !important;
          color: #78716c !important;
        }
        .leaflet-control-attribution a {
          color: #57534e !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    // Dynamic import of Leaflet to avoid SSR issues
    const initMap = async () => {
      if (typeof window === 'undefined' || !mapContainerRef.current || mapRef.current) return;

      const L = (await import('leaflet')).default;
      // @ts-expect-error - Leaflet CSS import for side effects only
      await import('leaflet/dist/leaflet.css');

      // Create map instance
      const map = L.map(mapContainerRef.current, {
        center: [office.latitude, office.longitude],
        zoom,
        zoomControl: false,
        scrollWheelZoom: false,
        attributionControl: false,
      });

      // CartoDB Positron - greyscale tile layer
      // This provides a minimal, elegant greyscale base map
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 20,
        subdomains: 'abcd',
      }).addTo(map);

      // Custom stone-themed marker
      const markerIcon = L.divIcon({
        className: 'stone-marker',
        html: `
          <div class="stone-marker-wrapper">
            <div class="stone-marker-pulse"></div>
            <div class="stone-marker-pin">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
          </div>
        `,
        iconSize: [60, 60],
        iconAnchor: [30, 60],
      });

      // Add marker
      L.marker([office.latitude, office.longitude], { icon: markerIcon }).addTo(map);

      // Add zoom control to bottom right
      L.control.zoom({ position: 'bottomright' }).addTo(map);

      // Add attribution
      L.control.attribution({ position: 'bottomleft', prefix: false })
        .addAttribution('&copy; <a href="https://carto.com/">CARTO</a>')
        .addTo(map);

      mapRef.current = map;
      setMapLoaded(true);
    };

    initMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [office.latitude, office.longitude, zoom]);

  return (
    <div
      className={`relative bg-stone-200 overflow-hidden group ${className}`}
      style={{ height }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >

      {/* Map container */}
      <div
        ref={mapContainerRef}
        className="absolute inset-0 z-0"
        style={{ height: '100%' }}
      />

      {/* Loading placeholder */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-stone-200 z-10">
          <div className="text-center">
            <div className="w-8 h-8 bg-stone-400 animate-pulse mx-auto mb-3" />
            <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
              {translations?.loadingMap || 'Loading map...'}
            </p>
          </div>
        </div>
      )}

      {/* Office Info Label - appears on hover */}
      <div
        className={`
          absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm px-6 py-4 
          border-l-2 border-stone-900 shadow-xl z-20
          transition-all duration-500 ease-out
          ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
        `}
      >
        <p
          className="font-serif text-xl text-stone-900"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {office.name}
        </p>
        <p className="text-xs uppercase tracking-widest text-stone-600 mt-1 font-sans">
          {office.location}
        </p>
        <p className="text-sm text-stone-500 mt-2 font-sans">
          {office.address}
        </p>
      </div>

      {/* View in Google Maps link */}
      <a
        href={googleMapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`
          absolute top-4 right-4 px-4 py-2 bg-white/95 backdrop-blur-sm 
          text-xs uppercase tracking-widest font-sans text-stone-600 
          hover:bg-stone-900 hover:text-white transition-all duration-300 z-20
          flex items-center gap-2
          ${isHovered ? 'opacity-100' : 'opacity-70'}
        `}
      >
        <span>{translations?.directions || 'Directions'}</span>
        <ExternalLink size={12} />
      </a>

      {/* Pin indicator when not hovered (subtle) */}
      {!isHovered && mapLoaded && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
          <div className="w-3 h-3 bg-stone-900 rounded-full animate-ping opacity-50" />
        </div>
      )}
    </div>
  );
}

export default StoneOfficeMap;
